import { Box, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { FieldStatusDto, SourcingEventV2Dto } from "../../../../../../autogen/bff-api";
import { TipTap, isTiptapContentEmpty } from "../../../../../../common/input/TipTap/TipTap";
import type {
  ReadinessLookup,
  KnownFieldKey,
} from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../wizardSteps";
import { isTwoStageProcedure } from "../../utils/procedure";
import { EmptyValue, SummaryField } from "./SummaryField";
import { SummaryFieldGroup } from "./SummaryFieldGroup";
import { SummarySection } from "./SummarySection";

export const FIELD_KEYS = [
  "ProcedureTypeSet",
  "ProcedureSupportedByProfile",
  "ProcedureRequiresPartIII",
  "CandidateMinimumAboveFloor",
  "CandidateMaximumNotBelowMinimum",
  "CandidateSelectionCriteriaPresent",
  "TenderValidityMonthsSet",
  "SubmissionLanguagesSet",
  "SubmissionLanguagesValid",
  "SubmissionLanguagesIncludesEng",
] as const satisfies readonly KnownFieldKey[];

const LANGUAGE_LABELS: Record<string, string> = {
  NOR: "Norwegian",
  ENG: "English",
};

interface ProcedureSectionProps {
  event: SourcingEventV2Dto;
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

export const ProcedureSection = ({
  event,
  readinessFor,
  onWizardStepNavigate,
}: ProcedureSectionProps) => {
  const { t } = useTranslation();
  const { publicContext } = event;
  const validityMonths = publicContext.eFormsNoticeData?.tenderValidityMonths;
  const submissionLanguages = publicContext.eFormsNoticeData?.submissionLanguages ?? [];

  const fieldStatuses = FIELD_KEYS.map(readinessFor).filter(
    (s): s is FieldStatusDto => s !== undefined,
  );

  return (
    <SummarySection
      title={t("Procedure")}
      editTarget={{ kind: "wizard-step", step: "procedure" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      <SummaryFieldGroup>
        <SummaryField
          label={t("Procedure type")}
          value={
            publicContext.procedureType
              ? t(publicContext.procedureType)
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("ProcedureTypeSet")}
          extraStatuses={[
            readinessFor("ProcedureSupportedByProfile"),
            readinessFor("ProcedureRequiresPartIII"),
          ]}
        />
        {isTwoStageProcedure(publicContext.procedureType) && (
          <SummaryField
            label={t("Limit on invited suppliers")}
            value={
              publicContext.candidateLimits ? (
                <Box>
                  <Text>
                    {`${publicContext.candidateLimits.minCandidates}${
                      publicContext.candidateLimits.maxCandidates != null
                        ? `–${publicContext.candidateLimits.maxCandidates}`
                        : "+"
                    }`}
                  </Text>
                  {isTiptapContentEmpty(publicContext.candidateLimits.selectionCriteria) ? (
                    <EmptyValue text={t("Not set")} />
                  ) : (
                    <Box mt="1" fontSize="sm" color="smMuted">
                      <TipTap
                        content={publicContext.candidateLimits.selectionCriteria}
                        editable={false}
                        hasBorder={false}
                        padding="0"
                        maxHeight=""
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                t("All qualified suppliers are invited")
              )
            }
            span="half"
            status={readinessFor("CandidateMinimumAboveFloor")}
            extraStatuses={[
              readinessFor("CandidateMaximumNotBelowMinimum"),
              readinessFor("CandidateSelectionCriteriaPresent"),
            ]}
          />
        )}
        <SummaryField
          label={t("Tender validity period")}
          value={
            isTwoStageProcedure(publicContext.procedureType)
              ? t("Set when the invitation to tender is sent")
              : validityMonths != null
                ? `${validityMonths} ${t("month", { count: validityMonths })}`
                : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("TenderValidityMonthsSet")}
        />
        <SummaryField
          label={t("Submission language")}
          value={
            submissionLanguages.length > 0
              ? submissionLanguages.map((code) => t(LANGUAGE_LABELS[code] ?? code)).join(", ")
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("SubmissionLanguagesSet")}
          extraStatuses={[
            readinessFor("SubmissionLanguagesValid"),
            readinessFor("SubmissionLanguagesIncludesEng"),
          ]}
        />
      </SummaryFieldGroup>
    </SummarySection>
  );
};
